import { observable, computed, action, makeObservable } from 'mobx';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import SafeMath from 'common/safeMath';

export class CountryBudget implements ICountryBudget {

    @observable isStockBudget: boolean;

    @observable isStockInUnitOnly: boolean;

    @observable totalAmount: number;

    @observable totalCurrentPay: number;

    @observable totalSpent: number;

    @observable currentIsoCurrencyCode: string;

    @observable amount: number;

    allowManagerOverBudget: boolean;

    requireCommentWhenOverBudget: boolean;

    isoCurrencyCode: string;

    budgetCycleId: number;

    budgetIsoCurrencyCode: string;

    name: string;

    private _currencyConversion: Nullable<CurrencyConversion>;

    constructor(
        countryBudgetDto: ICountryBudget,
        isoCurrencyCode: string,
        currencyConversion: Nullable<CurrencyConversion>) {
        makeObservable(this);
        this.budgetCycleId = countryBudgetDto.budgetCycleId;
        this.budgetIsoCurrencyCode = isoCurrencyCode;
        this.currentIsoCurrencyCode = isoCurrencyCode;
        this.name = countryBudgetDto.name;
        this.totalAmount = countryBudgetDto.totalAmount;
        this.totalCurrentPay = countryBudgetDto.totalCurrentPay;
        this.totalSpent = countryBudgetDto.totalSpent;
        this.amount = countryBudgetDto.amount || 0;
        this.isStockBudget = countryBudgetDto.isStockBudget;
        this.isStockInUnitOnly = countryBudgetDto.isStockInUnitOnly;
        this.allowManagerOverBudget = countryBudgetDto.allowManagerOverBudget;
        this.requireCommentWhenOverBudget = countryBudgetDto.requireCommentWhenOverBudget;
        this._currencyConversion = Coalesce.singleValue(currencyConversion, () => new CurrencyConversion(null));
    }

    @computed
    get convertedTotalAmount(): number {
        return this._currencyConversion?.convertCurrency(
            this.budgetIsoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.totalAmount) ?? 0;
    }

    @computed
    get convertedTotalCurrentPay(): number {
        return this._currencyConversion?.convertCurrency(
            this.budgetIsoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.totalCurrentPay) ?? 0;
    }

    @computed
    get convertedTotalSpent(): number {
        return this._currencyConversion?.convertCurrency(
            this.budgetIsoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.totalSpent) ?? 0;
    }

    @computed
    get convertedTotalRemaining(): number {
        return this._currencyConversion?.convertCurrency(
            this.budgetIsoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.totalRemaining) ?? 0;
    }

    @computed
    get totalPercentage(): number {
        return SafeMath.divide(this.totalAmount, this.totalCurrentPay);
    }

    @computed
    get isOverBudget(): boolean {
        return this.totalRemaining < 0;
    }

    @action
    addSpent(
        amountIsoCurrencyCode: string,
        previousAmountValue: number,
        currentAmountValue: number,
        updateNewPay: boolean,
        isHourly: boolean,
        annualStandardWorkHours: number,
        fullTimeEquivalent: number): void {
        const previousAnnualAmountInBudgetCurrency = this.getAnnualAmountValueInBudgetCurrency(
            amountIsoCurrencyCode,
            previousAmountValue,
            updateNewPay,
            isHourly,
            annualStandardWorkHours,
            fullTimeEquivalent);

        const currentAnnualAmountInBudgetCurrency = this.getAnnualAmountValueInBudgetCurrency(
            amountIsoCurrencyCode,
            currentAmountValue,
            updateNewPay,
            isHourly,
            annualStandardWorkHours,
            fullTimeEquivalent);

        const spent = currentAnnualAmountInBudgetCurrency - previousAnnualAmountInBudgetCurrency;
        this.totalSpent += spent;
    }

    @action
    addStockSpent(
        previousAmountValue: number,
        currentAmountValue: number): void {
        const spent = currentAmountValue - previousAmountValue;
        this.totalSpent += spent;
    }

    @action
    setCurrentCurrency(currentIsoCurrencyCode: string): void {
        if (!currentIsoCurrencyCode) {
            this.currentIsoCurrencyCode = this.budgetIsoCurrencyCode;
            return;
        }

        this.currentIsoCurrencyCode = currentIsoCurrencyCode;
    }

    @computed
    private get totalRemaining(): number {
        return this.totalAmount - this.totalSpent;
    }

    private getAnnualAmountValueInBudgetCurrency(
        amountIsoCurrencyCode: string,
        value: number,
        updateNewPay: boolean,
        isHourly: boolean,
        annualStandardWorkHours: number,
        fullTimeEquivalent: number): number {
        const annualAmountValue = updateNewPay && isHourly ?
            value * annualStandardWorkHours * fullTimeEquivalent :
            value;

        return this._currencyConversion?.convertCurrency(
            amountIsoCurrencyCode,
            this.budgetIsoCurrencyCode,
            annualAmountValue) ?? 0;
    }
}